import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Typography } from "@tento-ui/ui/typography";
import { Button } from "@tento-ui/components/button/button";
import { Documentation } from "@tento-ui/components/documentation/documentation";

/**
 * Detail card for a single scheduling proposal. Shows:
 *   - Rationale text (why this plan)
 *   - Assignment table (id / title / time)
 *   - `[ Schedule plan X ]` CTA
 *
 * Wrapped in a rounded container. Designed to be swapped out by the
 * parent ProposalList when the selected pill changes — call `bind()`
 * with the new proposal to rebind in-place.
 */
@component({ name: "std-proposal-detail" })
export class ProposalDetail extends LonaWebComponent {
  private $rationale: HTMLElement = this.$("rationale");
  private $table: HTMLElement = this.$("table");
  private $actionLabel: HTMLElement = this.$("action-label");

  private onSelect: Option<EmptyFunction> = null;

  bind(props: ProposalDetail.Props): this {
    this.onSelect = props.onSelect ?? null;

    // rationale
    DomUtils.clearChildren(this.$rationale);
    if (props.rationale) {
      const $p = Typography.p(props.rationale);
      $p.classList.add("rationale-text");
      this.$rationale.appendChild($p);
    }
    this.$rationale.toggleAttribute("hidden", !props.rationale);

    // assignment table
    DomUtils.clearChildren(this.$table);
    const $table = document.createElement("table");
    for (const a of props.assignments) {
      const $tr = document.createElement("tr");

      const $tdId = document.createElement("td");
      $tdId.classList.add("col-id");
      $tdId.textContent = a.displayId;

      const $tdTitle = document.createElement("td");
      $tdTitle.classList.add("col-title");
      $tdTitle.textContent = a.title;

      const $tdTime = document.createElement("td");
      $tdTime.classList.add("col-time");
      $tdTime.textContent = a.timeLabel;

      $tr.appendChild($tdId);
      $tr.appendChild($tdTitle);
      $tr.appendChild($tdTime);
      $table.appendChild($tr);
    }
    this.$table.appendChild($table);

    // CTA label
    this.$actionLabel.textContent = props.actionLabel ?? "Select";

    return this;
  }

  static makeWith(props: ProposalDetail.Props): ProposalDetail {
    return ProposalDetail.make().bind(props);
  }

  connectedCallback(): void {
    super.connectedCallback?.();
    GESTURE_MANAGER.addPointerEvent(this.$("select-btn"), {
      onClick: () => this.onSelect?.(),
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    GESTURE_MANAGER.unregister(this.$("select-btn"));
  }

  static $styles = [
    Button.$style,
    Documentation.$tableStyle,
    css`
      :host {
        display: block;
      }

      #root {
        display: flex;
        flex-direction: column;
        background: var(--bg);
        border-radius: 12px;
        padding: 12px;
        box-shadow: var(--box-shadow-light);
      }

      #rationale[hidden] {
        display: none;
      }

      .rationale-text {
        font-size: var(--text-15px);
        --line-height-ratio: 1.4;
        --margin-top: 8px;
        --margin-bottom: 8px;
        color: var(--text-sec);
      }

      /* Override Documentation.$tableStyle for compact context */
      table {
        margin-block: 12px;
      }

      th, td {
        font-size: var(--text-13px);
        padding: 6px 0px;
        border-bottom: 1px solid var(--divider);
      }

      tr:last-child td {
        border-bottom: none;
      }

      td.col-id {
        color: var(--text-ter);
        font-size: var(--text-13px);
        white-space: nowrap;
        width: 1%;
        padding-right: 8px;
        padding-left: 0px;
      }

      td.col-title {
        color: var(--text-pri);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 0;
        width: 100%;
      }

      td.col-time {
        color: var(--text-sec);
        font-size: var(--text-13px);
        white-space: nowrap;
        text-align: right;
        width: 1%;
        padding-left: 8px;
        padding-right: 0px;
      }

      #actions {
        display: flex;
        justify-content: flex-end;
      }

      #select-btn {
        --button-padding: 8px 16px;
      }
    `,
  ];

  static $html = html`
    <div id="root">
      <div id="rationale"></div>
      <div id="table"></div>
      <div id="actions">
        <button id="select-btn" primary><span id="action-label"></span></button>
      </div>
    </div>
  `;
}

export namespace ProposalDetail {
  export interface Assignment {
    displayId: string;
    title: string;
    timeLabel: string;
  }

  export type Props = {
    rationale?: Option<string>;
    assignments: Assignment[];
    actionLabel?: Option<string>;
    onSelect?: Option<EmptyFunction>;
  };
}
